import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';

export interface WorkInterval {
  weekday: number;
  start: string;
  end: string;
}

interface OrgSchedule {
  id: string;
  name: string;
  groupName: string;
  timezone: string;
  countryCode: string | null;
  custom: boolean;
  intervals: WorkInterval[];
}

const KEY = ['parametros', 'calendarios'] as const;
const DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** Horario laboral de cada organización (guía 7.1): de él sale el SLA en horas hábiles. */
export function CalendarSection() {
  const schedules = useQuery({ queryKey: KEY, queryFn: () => api<OrgSchedule[]>('/api/parametros/calendarios') });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!schedules.data) return schedules.error ? <Alert>{errorMessage(schedules.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const selected = schedules.data.find((o) => o.id === selectedId);
  const groups = [...new Set(schedules.data.map((o) => o.groupName))];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t('cal.titulo')}</h2>
        <p className="max-w-2xl text-sm text-slate-600">{t('cal.descripcion')}</p>
      </div>
      <div className="grid gap-4 xl:grid-cols-[18rem_1fr]">
        <Card className="self-start p-2">
          {groups.map((g) => (
            <div key={g} className="mb-2">
              <h3 className="px-3 pb-1 pt-2 text-xs font-semibold text-slate-500">{g}</h3>
              <ul aria-label={g}>
                {schedules.data
                  .filter((o) => o.groupName === g)
                  .map((o) => (
                    <li key={o.id}>
                      <button
                        type="button"
                        aria-current={o.id === selectedId ? 'true' : undefined}
                        className={`flex w-full items-center justify-between gap-2 rounded px-3 py-2 text-left text-sm hover:bg-slate-50 ${o.id === selectedId ? 'bg-slate-100' : ''}`}
                        onClick={() => setSelectedId(o.id)}
                      >
                        <span>{o.name}</span>
                        <Badge tone={o.custom ? 'blue' : 'slate'}>{o.custom ? t('cal.propio') : t('cal.por_defecto')}</Badge>
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </Card>
        {selected ? <ScheduleEditor key={`${selected.id}-${String(selected.custom)}`} org={selected} /> : <p className="text-sm text-slate-600">{t('cal.elegir')}</p>}
      </div>
    </div>
  );
}

/** Tramo en edición, con una clave estable: editar una hora no debe mover el tramo ni perder el foco. */
type Slot = WorkInterval & { key: number };

function ScheduleEditor({ org }: { org: OrgSchedule }) {
  const qc = useQueryClient();
  const [slots, setSlots] = useState<Slot[]>(() => org.intervals.map((i, key) => ({ ...i, key })));
  const nextKey = useRef(org.intervals.length);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const path = `/api/parametros/calendarios/${org.id}`;

  const ofDay = (day: number) => slots.filter((i) => i.weekday === day);
  const newSlots = (items: WorkInterval[]): Slot[] => items.map((i) => ({ ...i, key: nextKey.current++ }));
  const edit = (key: number, patch: Partial<WorkInterval>) => setSlots((all) => all.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const remove = (key: number) => setSlots((all) => all.filter((s) => s.key !== key));
  const add = (day: number) => {
    const created = newSlots([{ weekday: day, start: ofDay(day).at(-1)?.end ?? '13:00', end: '17:00' }]);
    setSlots((all) => [...all, ...created]);
  };
  const toggleDay = (day: number, on: boolean) => {
    const created = on ? newSlots([{ weekday: day, start: '08:00', end: '17:00' }]) : [];
    setSlots((all) => [...all.filter((s) => s.weekday !== day), ...created]);
  };
  const copyMonday = () => {
    const created = newSlots([2, 3, 4, 5].flatMap((d) => ofDay(1).map((s) => ({ weekday: d, start: s.start, end: s.end }))));
    setSlots((all) => [...all.filter((s) => s.weekday === 1 || s.weekday > 5), ...created]);
  };
  /** Lo que se guarda: ordenado por día y hora, sin la clave de edición. */
  const intervals = (): WorkInterval[] =>
    [...slots].sort((a, b) => a.weekday - b.weekday || a.start.localeCompare(b.start)).map(({ weekday, start, end }) => ({ weekday, start, end }));

  async function act(fn: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: KEY });
      setNotice(message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={org.name}>
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          {t('cal.zona', { zona: org.timezone })} {org.custom ? '' : t('cal.usa_defecto')}
        </p>
        {error && <Alert>{error}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}
        <ul className="divide-y divide-slate-100" aria-label={t('cal.dias')}>
          {DAYS.map((day) => {
            const daySlots = ofDay(day);
            const dayName = t(`cal.dia_${day}` as 'cal.dia_1');
            return (
              <li key={day} className="flex flex-wrap items-center gap-3 py-2">
                <div className="w-32">
                  <Checkbox label={dayName} checked={daySlots.length > 0} onChange={(e) => toggleDay(day, e.target.checked)} />
                </div>
                {daySlots.length === 0 ? (
                  <span className="text-sm text-slate-400">{t('cal.no_laborable')}</span>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    {daySlots.map((slot, k) => (
                      <span key={slot.key} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1">
                        <input
                          type="time"
                          aria-label={t('cal.desde_dia', { dia: dayName, n: k + 1 })}
                          className="rounded border border-slate-300 px-1 py-0.5 text-sm"
                          value={slot.start}
                          onChange={(e) => edit(slot.key, { start: e.target.value })}
                        />
                        <span className="text-slate-400">–</span>
                        <input
                          type="time"
                          aria-label={t('cal.hasta_dia', { dia: dayName, n: k + 1 })}
                          className="rounded border border-slate-300 px-1 py-0.5 text-sm"
                          value={slot.end}
                          onChange={(e) => edit(slot.key, { end: e.target.value })}
                        />
                        <button
                          type="button"
                          className="px-1 text-slate-400 hover:text-red-700"
                          aria-label={t('cal.quitar_tramo', { dia: dayName, n: k + 1 })}
                          onClick={() => remove(slot.key)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <Button
                      variant="link"
                      className="text-xs"
                      onClick={() => add(day)}
                    >
                      {t('cal.agregar_tramo')}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap items-center gap-2">
          <Button disabled={busy} onClick={() => void act(() => api(path, { method: 'PUT', json: { intervals: intervals() } }), t('cal.guardado'))}>
            {t('comun.guardar')}
          </Button>
          <Button variant="secondary" disabled={busy || ofDay(1).length === 0} onClick={copyMonday}>
            {t('cal.copiar_lunes')}
          </Button>
          {org.custom && (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  await api(path, { method: 'DELETE' });
                }, t('cal.restablecido'))
              }
            >
              {t('cal.restablecer')}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
